import type { ColumnsType } from 'antd/es/table';
import AccessBadge from '../../../../shared/components/AccessBadge';
import RowOptionsMenu from '../../../../shared/components/RowOptionsMenu';
import TruncatedText from '../../../../shared/components/TruncatedText';
import type { GroupMember } from '../../../../services/GroupManager/groupManager.service';
import styles from './GroupTables.module.scss';

export enum GroupMemberAction {
  REMOVE = 'remove',
  VIEW_PROFILE = 'view-profile',
  DUPLICATE = 'duplicate',
  MOVE = 'move',
}

const GROUP_PICKER_OPTIONS = (groups: Array<{ id: number; name: string }>, currentGroupId: number) =>
  groups
    .filter((group) => group.id !== currentGroupId)
    .map((group) => ({
      id: String(group.id),
      label: group.name,
    }));

const ROW_OPTIONS = (groups: Array<{ id: number; name: string }>, currentGroupId: number) => [
  { id: GroupMemberAction.REMOVE, label: 'REMOVE USER FROM GROUP' },
  { id: GroupMemberAction.VIEW_PROFILE, label: 'VIEW USER PROFILE' },
  {
    id: GroupMemberAction.DUPLICATE,
    label: 'COPY USER TO GROUP',
    panel: {
      header: 'COPY TO GROUP',
      actionId: GroupMemberAction.DUPLICATE,
      showBack: true,
      options: GROUP_PICKER_OPTIONS(groups, currentGroupId),
    },
  },
  {
    id: GroupMemberAction.MOVE,
    label: 'MOVE USER',
    panel: {
      header: 'MOVE USER TO',
      actionId: GroupMemberAction.MOVE,
      showBack: true,
      options: GROUP_PICKER_OPTIONS(groups, currentGroupId),
    },
  },
];

const COLUMN_WIDTH = {
  name: 180,
  title: 180,
  email: 220,
  access: 128,
  groups: 320,
  actions: 48,
};

const compareText = (left: string, right: string) => left.localeCompare(right);

export const buildColumns = (
  groups: Array<{ id: number; name: string }>,
  currentGroupId: number,
  onAction: (
    action: GroupMemberAction,
    member: GroupMember,
    anchor: HTMLElement | null,
    targetGroupId?: number,
  ) => void,
): ColumnsType<GroupMember> => {
  const rowOptions = ROW_OPTIONS(groups, currentGroupId);

  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: COLUMN_WIDTH.name,
      sorter: (left, right) => compareText(left.name, right.name),
      ellipsis: { showTitle: false },
      render: (value: GroupMember['name']) => <TruncatedText text={value} />,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: COLUMN_WIDTH.title,
      sorter: (left, right) => compareText(left.title, right.title),
      ellipsis: { showTitle: false },
      render: (value: GroupMember['title']) => <TruncatedText text={value} />,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: COLUMN_WIDTH.email,
      sorter: (left, right) => compareText(left.email, right.email),
      ellipsis: { showTitle: false },
      render: (value: GroupMember['email']) => <TruncatedText text={value} />,
    },
    {
      title: 'Access',
      dataIndex: 'access',
      key: 'access',
      width: COLUMN_WIDTH.access,
      sorter: (left, right) => compareText(left.access, right.access),
      render: (value: GroupMember['access']) => <AccessBadge role={value} />,
    },
    {
      title: 'Groups',
      dataIndex: 'groups',
      key: 'groups',
      width: COLUMN_WIDTH.groups,
      ellipsis: { showTitle: false },
      render: (value: GroupMember['groups']) => <TruncatedText text={value.map((group) => group.name).join(', ')} />,
    },
    {
      title: '',
      key: 'actions',
      width: COLUMN_WIDTH.actions,
      fixed: 'right',
      render: (_, record) => (
        <span className={styles.actionCell}>
          <RowOptionsMenu
            options={rowOptions}
            header="USER OPTIONS"
            onSelect={(actionId, trigger, parentActionId) =>
              onAction(
                (parentActionId ?? actionId) as GroupMemberAction,
                record,
                trigger,
                parentActionId ? Number(actionId) : undefined,
              )
            }
          />
        </span>
      ),
    },
  ];
};

export const TABLE_MIN_WIDTH_PX = Object.values(COLUMN_WIDTH).reduce((total, width) => total + width, 0);
const TABLE_ROW_HEIGHT_PX = 56;
const TABLE_HEADER_HEIGHT_PX = 48;
const MAX_VISIBLE_ROWS = 6;

// The body grows with its content until six rows, then becomes independently scrollable.
export const tableBodyHeight = (rowCount: number) => TABLE_ROW_HEIGHT_PX * Math.min(Math.max(rowCount, 1), MAX_VISIBLE_ROWS);
export const tableHeight = (rowCount: number) => TABLE_HEADER_HEIGHT_PX + tableBodyHeight(rowCount);
